import { css } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { HeatMapStyle } from '~/_shared/types/heatmap/heatMap.enum';
import {
  GroupRadiusEditLevel, ProximityType,
} from '~/_shared/types/proximity/proximity.enums';
import { useTranslation } from '~/_shared/utils/hooks';
import { BoundaryGroupCodenames } from '~/boundary/boundary.types';
import { type ModalProps } from '~/modal/modalType.enum';
import { CreateCustomBoundaryMode } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createCustomBoundaryGroupPane.component';
import { BoundaryActionType } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/loadBoundaryPane.component';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  clearActiveMapTemplate, closeLeftSidebar, setActiveMapTemplate, setMapSettingsInitialExpandedIndices,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { boundaryDrawCloseModal } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import {
  boundaryFormReset, boundaryFormSetPreselected, boundaryFormSetType, boundaryFormSetTypeAndMode,
} from '~/store/frontendState/mapTools/boundary/boundaryForm/boundaryForm.actionCreators';
import { boundarySelectEditCloseModal } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  heatmapFormInitialStyleSet, heatmapFormReset,
} from '~/store/frontendState/mapTools/heatmap/heatmapForm.actionCreators';
import { frontendStateSearchHide } from '~/store/frontendState/mapTools/search/search.actionCreators';
import {
  proximityToolReset, setApplyTo, setProximityWithin, setTravelTimeHours, setTravelTimeMinutes, setType,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import {
  IMAGE_PADDING, IMAGE_WIDTH, MapTemplatesComponent,
} from './mapTemplates.component';
import { MapTemplates } from './mapTemplates.helpers';

const IMAGES_PER_ROW = 4;
const CONTENT_PADDING = 24;
const MODAL_WIDTH = IMAGES_PER_ROW * (IMAGE_WIDTH + IMAGE_PADDING) + CONTENT_PADDING * 2;

const modalContentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: CONTENT_PADDING,
});

export type MapTemplatesProps = ModalProps;

export const MapTemplatesContainer = ({ isOpen, onClose }: MapTemplatesProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const handleCloseAndResets = () => {
    dispatch(boundaryDrawCloseModal());
    dispatch(boundaryFormReset());
    dispatch(boundarySelectEditCloseModal());
    dispatch(clearActiveMapTemplate());
    dispatch(closeLeftSidebar());
    dispatch(frontendStateSearchHide());
    dispatch(heatmapFormReset());
    dispatch(proximityToolReset());
    onClose();
  };

  const callbacks: Record<MapTemplates, () => void> = {
    [MapTemplates.ATB]: () => {
      handleCloseAndResets();
      dispatch(boundaryFormSetTypeAndMode(BoundaryActionType.CreateTerritories, CreateCustomBoundaryMode.WMSOptimized));
      dispatch(setActiveMapTemplate(MapTemplates.ATB));
    },
    [MapTemplates.BoundariesDemographic]: () => {
      handleCloseAndResets();
      dispatch(boundaryFormReset());
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Demographic,
        demographicName: 'Total Population',
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesDemographic));
    },
    [MapTemplates.BoundariesFill]: () => {
      handleCloseAndResets();
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Numeric,
        demographicName: null,
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesFill));
    },
    [MapTemplates.BoundariesPlain]: () => {
      handleCloseAndResets();
      dispatch(boundaryFormSetType(BoundaryActionType.LoadGeographicBoundary));
      dispatch(boundaryFormSetPreselected({
        boundaryGroupCodename: BoundaryGroupCodenames.UsZipCodes,
        boundaryTerritoryType: BoundaryTerritoryType.Manual,
        demographicName: null,
      }));
      dispatch(setActiveMapTemplate(MapTemplates.BoundariesPlain));
    },
    [MapTemplates.DTP]: () => {
      handleCloseAndResets();
      dispatch(setType(ProximityType.DriveTimePolygon));
      dispatch(setTravelTimeHours(1));
      dispatch(setTravelTimeMinutes(10));
      dispatch(setActiveMapTemplate(MapTemplates.DTP));
    },
    [MapTemplates.Filter]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.Filter));
    },
    [MapTemplates.Grouping]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.Grouping));
    },
    [MapTemplates.Heatmaps]: () => {
      handleCloseAndResets();
      dispatch(heatmapFormInitialStyleSet(HeatMapStyle.NumericalData));
      dispatch(setActiveMapTemplate(MapTemplates.Heatmaps));
    },
    [MapTemplates.Layering]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.Layering));
    },
    [MapTemplates.LocationFind]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.LocationFind));
    },
    [MapTemplates.POI]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.POI));
    },
    [MapTemplates.Radii]: () => {
      handleCloseAndResets();
      dispatch(setType(ProximityType.DistanceRadius));
      dispatch(setApplyTo(GroupRadiusEditLevel.Group));
      dispatch(setProximityWithin(50));
      dispatch(setActiveMapTemplate(MapTemplates.Radii));
    },
    [MapTemplates.Routes]: () => {
      handleCloseAndResets();
      dispatch(setActiveMapTemplate(MapTemplates.Routes));
    },
    [MapTemplates.Skip]: handleCloseAndResets,
    [MapTemplates.Styling]: () => {
      handleCloseAndResets();
      dispatch(setMapSettingsInitialExpandedIndices([0, 1]));
      dispatch(setActiveMapTemplate(MapTemplates.Styling));
    },
    [MapTemplates.TerritoryManual]: () => {
      handleCloseAndResets();
      dispatch(boundaryFormSetTypeAndMode(BoundaryActionType.CreateTerritories, CreateCustomBoundaryMode.Manual));
      dispatch(setActiveMapTemplate(MapTemplates.TerritoryManual));
    },
  };

  return (
    <ModalComponent
      contentStyle={modalContentStyle}
      maxWidth={MODAL_WIDTH}
      caption={t('MapTemplates.caption')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <MapTemplatesComponent
        callbacks={callbacks}
      />
    </ModalComponent>
  );
};
